import React, { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import ConfirmSubmitModal from "./ConfirmSubmitModal";

const EditDefaultPaymentPopup = ({
    selectedTransaction,
    grantList,
    onSave,
    onCancel,
}) => {
    const [defaultPayment, setDefaultPayment] = useState(
        typeof selectedTransaction.defaultPayment === "string"
            ? JSON.parse(selectedTransaction.defaultPayment || "{}")
            : selectedTransaction.defaultPayment || {}
    );

    const handlePercentageChange = (grant, percentage) => {
        setDefaultPayment((prev) => ({
            ...prev,
            [grant]: percentage,
        }));
    };

    const handleSubmit = () => {
        const totalPercentage = Object.values(defaultPayment).reduce(
            (sum, value) => sum + parseFloat(value || 0),
            0
        );

        if (Math.abs(totalPercentage - 100) > 0.01) {
            alert(
                `Total percentage must equal 100%. Current total: ${totalPercentage.toFixed(
                    2
                )}%`
            );
            return;
        }
        onSave({ ...selectedTransaction, defaultPayment });
    };

    return (
        <div className="card p-6 max-w-md mx-auto animate-fade-in">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Edit Payment</h3>
            <div className="space-y-4">
                {grantList.map((grant) => (
                    <div key={grant} className="space-y-2">
                        <label className="block text-sm font-medium text-gray-700">{grant}</label>
                        <input
                            type="number"
                            value={defaultPayment[grant] || ""}
                            onChange={(e) =>
                                handlePercentageChange(grant, e.target.value)
                            }
                            min="0"
                            max="100"
                            step="0.01"
                            className="input-field"
                            placeholder="0.00"
                        />
                    </div>
                ))}
            </div>
            <div className="flex gap-3 mt-6">
                <button onClick={handleSubmit} className="btn-primary flex-1">Save</button>
                <button onClick={onCancel} className="btn-secondary flex-1">Cancel</button>
            </div>
        </div>
    );
};

const DepositTable = ({ depositTransactions, initialFilterStartDate, initialFilterEndDate }) => {
    const [filteredTransactions, setFilteredTransactions] = useState([]);
    const [filterPeriod, setFilterPeriod] = useState('all');
    const [filterStartDate, setFilterStartDate] = useState('');
    const [filterEndDate, setFilterEndDate] = useState('');
    const [showFilterPopup, setShowFilterPopup] = useState(false);
    const [showConfirmedRecords, setShowConfirmedRecords] = useState(true);
    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const [customizedCategories, setCustomizedCategories] = useState([]);
    const [showEditDefaultPaymentPopup, setShowEditDefaultPaymentPopup] =
        useState(false);
    const [selectedTransaction, setSelectedTransaction] = useState(null);
    const [grantList, setGrantList] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [depositTypes, setDepositTypes] = useState([]);
    const [categoriesByDepositType, setCategoriesByDepositType] = useState({});
    const fixedDepositTypes = [
        'Grant Funding', 'Revenue', 'Investment', 'Loan',
        'Shareholder Injection', 'PMS Payment', 'Other',
        'Refund (Grant Item)', 'Refund (Non-grant Item)',
        'Internal Bank Transfer', 'N/A (Non-grant Item)',
    ];
    const depositCategories = [...new Set([...fixedDepositTypes, ...depositTypes])];
    const [showEditNotePopup, setShowEditNotePopup] = useState(false);
    const [transactionToEditNote, setTransactionToEditNote] = useState(null);
    const [showBulkCategoryPopup, setShowBulkCategoryPopup] = useState(false);
    const [bulkCategoryValue, setBulkCategoryValue] = useState("");

    // Itemization state
    const [expandedItemRows, setExpandedItemRows] = useState({});
    const toggleItemization = (transactionId) => { setExpandedItemRows(prev => ({ ...prev, [transactionId]: !prev[transactionId] })); };
    const getLineItems = (transaction) => { try { return JSON.parse(transaction["Line Items"] || "[]"); } catch { return []; } };
    const addLineItem = async (transaction) => {
        const items = getLineItems(transaction);
        items.push({ id: Date.now().toString(), name: '', description: '', amount: '', depositCategory: '', customizedCategory: 'N/A', payment: {} });
        await saveLineItems(transaction, items);
    };
    const updateLineItem = async (transaction, itemId, field, value) => {
        const items = getLineItems(transaction);
        const idx = items.findIndex(i => i.id === itemId);
        if (idx === -1) return;
        items[idx][field] = value;
        await saveLineItems(transaction, items);
    };
    const removeLineItem = async (transaction, itemId) => { await saveLineItems(transaction, getLineItems(transaction).filter(i => i.id !== itemId)); };
    const saveLineItems = async (transaction, items) => {
        try {
            const response = await fetch(
                `${process.env.REACT_APP_BACKEND_API_URL}/deposit/${transaction.ID}`,
                { method: "PUT", headers: { "Content-Type": "application/json" }, credentials: "include",
                  body: JSON.stringify({ ...transaction, "Line Items": JSON.stringify(items) }) }
            );
            if (response.ok) { setFilteredTransactions(prev => prev.map(t => t.ID === transaction.ID ? { ...t, "Line Items": JSON.stringify(items) } : t)); }
        } catch (error) { console.error("Error saving line items:", error); }
    };

    useEffect(() => {
        if (initialFilterStartDate && initialFilterEndDate) {
            setFilterStartDate(initialFilterStartDate);
            setFilterEndDate(initialFilterEndDate);
            setFilterPeriod('custom');
        }
    }, [initialFilterStartDate, initialFilterEndDate]);

    // Add this handler function
    const handleEditNoteClick = (transaction) => {
        setTransactionToEditNote(transaction);
        setShowEditNotePopup(true);
    };

    // Add this handler for saving the note
    const handleSaveNote = async () => {
        try {
            const response = await fetch(
                `${process.env.REACT_APP_BACKEND_API_URL}/deposit/note/${transactionToEditNote.ID}`,
                {
                    method: "PUT",
                    headers: {
                        "Content-Type": "application/json",
                    },
                    credentials: 'include',
                    body: JSON.stringify({ Note: transactionToEditNote.Note }),
                }
            );

            if (response.ok) {
                // Update the local state to reflect the changes
                const updatedTransactions = filteredTransactions.map((t) =>
                    t.ID === transactionToEditNote.ID
                        ? { ...t, Note: transactionToEditNote.Note }
                        : t
                );
                setFilteredTransactions(updatedTransactions);
                setShowEditNotePopup(false);
            } else {
                throw new Error("Failed to update note");
            }
        } catch (error) {
            console.error("Error updating note:", error);
            alert("Error updating note.");
        }
    };

    const fetchGrantList = useCallback(async () => {
        try {
            const response = await fetch(
                `${process.env.REACT_APP_BACKEND_API_URL}/grants/grant-list`,
                {
                    credentials: 'include'
                }
            );
            if (!response.ok) {
                throw new Error(`HTTP error! Status: ${response.status}`);
            }
            const grantData = await response.json();
            setGrantList(grantData);
        } catch (error) {
            console.error("Error fetching grant list:", error);
        }
    }, []);

    const fetchCustomizedCategories = useCallback(async () => {
        try {
            const response = await fetch(
                `${process.env.REACT_APP_BACKEND_API_URL}/customized-categories`,
                {
                    credentials: 'include'
                }
            );
            if (!response.ok) {
                throw new Error(`HTTP error! Status: ${response.status}`);
            }
            const categories = await response.json();
            setCustomizedCategories(categories);
        } catch (error) {
            console.error("Error fetching customized categories:", error);
        }
    }, []);

    const fetchDepositTypes = useCallback(async () => {
        try {
            const response = await fetch(
                `${process.env.REACT_APP_BACKEND_API_URL}/deposit-categories/deposit-types`,
                { credentials: 'include' }
            );
            if (!response.ok) throw new Error(`HTTP error! Status: ${response.status}`);
            const data = await response.json();
            setDepositTypes(data);
        } catch (error) {
            console.error('Error fetching deposit types:', error);
        }
    }, []);

    const fetchCategoriesByDepositType = useCallback(async () => {
        try {
            const response = await fetch(
                `${process.env.REACT_APP_BACKEND_API_URL}/deposit-categories/by-deposit-type`,
                { credentials: 'include' }
            );
            if (!response.ok) throw new Error(`HTTP error! Status: ${response.status}`);
            const data = await response.json();
            setCategoriesByDepositType(data);
        } catch (error) {
            console.error('Error fetching categories by deposit type:', error);
        }
    }, []);

    useEffect(() => {
        fetchCustomizedCategories();
        fetchGrantList();
        fetchDepositTypes();
        fetchCategoriesByDepositType();
    }, [fetchCustomizedCategories, fetchGrantList, fetchDepositTypes, fetchCategoriesByDepositType]);

    useEffect(() => {
        filterTransactions();
        if (depositTransactions && depositTransactions.length >= 0) {
            setIsLoading(false);
        }
    }, [depositTransactions, showConfirmedRecords, filterPeriod, filterStartDate, filterEndDate]);

    const filterTransactions = () => {
        let filtered = depositTransactions;

        filtered = filtered.filter((t) => t.Confirmed !== "Yes");

        if (filterPeriod !== 'all') {
            const currentDate = new Date();
            let startDate, endDate;
            
            switch (filterPeriod) {
              case "lastMonth":
                startDate = new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1);
                endDate = new Date(currentDate.getFullYear(), currentDate.getMonth(), 0);
                endDate.setHours(23, 59, 59, 999);
                break;
              case "lastQuarter":
                startDate = new Date(currentDate.getFullYear(), currentDate.getMonth() - 3, 1);
                endDate = new Date(currentDate.getFullYear(), currentDate.getMonth(), 0);
                endDate.setHours(23, 59, 59, 999);
                break;
              case "custom":
                startDate = new Date(filterStartDate);
                endDate = new Date(filterEndDate);
                endDate.setHours(23, 59, 59, 999);
                break;
              default:
                if (!isNaN(parseInt(filterPeriod))) {
                  // Year filtering
                  startDate = new Date(parseInt(filterPeriod), 0, 1);
                  endDate = new Date(parseInt(filterPeriod), 11, 31);
                  endDate.setHours(23, 59, 59, 999);
                }
            }
            
            if (startDate && endDate) {
              filtered = filtered.filter((t) => {
                const transactionDate = new Date(t["Date (UTC)"]);
                return transactionDate >= startDate && transactionDate <= endDate;
              });
            }
        }

        setFilteredTransactions(filtered);
    };

    const getFilterPeriodOptions = () => {
        const years = [...new Set(depositTransactions.map(t => 
          new Date(t["Date (UTC)"]).getFullYear()
        ))];
        
        return [
          { value: "all", label: "All" },
          ...years.map(year => ({ value: year.toString(), label: year.toString() })),
          { value: "lastMonth", label: "Last Month" },
          { value: "lastQuarter", label: "Last Quarter" },
          { value: "custom", label: "Custom Period" },
        ];
    };

    const handleFilterPeriodChange = (e) => {
        const period = e.target.value;
        setFilterPeriod(period);
        
        // Show popup for custom period
        if (period === 'custom') {
          setShowFilterPopup(true);
        }
    };

    const handleCheckboxChange = async (id, checked) => {
        try {
            const response = await fetch(
                `${process.env.REACT_APP_BACKEND_API_URL}/deposit/check/${id}`,
                {
                    method: "PUT",
                    headers: {
                        "Content-Type": "application/json",
                    },
                    credentials: 'include',
                    body: JSON.stringify({ Checked: checked }),
                }
            );
            if (!response.ok) {
                throw new Error(`HTTP error! Status: ${response.status}`);
            }
            const updatedTransactions = filteredTransactions.map((t) =>
                t.ID === id ? { ...t, Checked: checked ? "Yes" : "No" } : t
            );
            setFilteredTransactions(updatedTransactions);
        } catch (error) {
            console.error("Error updating check status:", error);
            alert("Error updating check status.");
        }
    };

    const handleSelectAllVisible = async (shouldCheck) => {
        const targets = filteredTransactions.filter(t => t.ID && (t.Checked === "Yes") !== shouldCheck);
        if (targets.length === 0) return;
        try {
            const ids = targets.map(t => t.ID);
            const resp = await fetch(`${process.env.REACT_APP_BACKEND_API_URL}/deposit/bulk-update`, {
                method: "POST",
                credentials: 'include',
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ ids, Checked: shouldCheck })
            });
            if (!resp.ok) throw new Error('Bulk update failed');
            setFilteredTransactions(prev => prev.map(t =>
                targets.find(x => x.ID === t.ID) ? { ...t, Checked: shouldCheck ? "Yes" : "No" } : t
            ));
        } catch (error) {
            console.error("Error updating bulk check status:", error);
            alert("Error updating bulk check status.");
        }
    };

    const handleBulkSetCategory = async () => {
        if (!bulkCategoryValue) {
            alert("Please select a deposit type.");
            return;
        }
        const targets = filteredTransactions.filter(t => t.ID && t.Confirmed !== "Yes" && getLineItems(t).length === 0);
        if (targets.length === 0) {
            alert("No unconfirmed transactions to update (line-itemized rows are skipped).");
            return;
        }
        try {
            const ids = targets.map(t => t.ID);
            const resp = await fetch(`${process.env.REACT_APP_BACKEND_API_URL}/deposit/bulk-update`, {
                method: "POST",
                credentials: 'include',
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ ids, depositCategory: bulkCategoryValue })
            });
            if (!resp.ok) throw new Error('Bulk update failed');
            // Refresh local state
            setFilteredTransactions(prev => prev.map(t =>
                ids.includes(t.ID) ? { ...t, "Deposit Category": bulkCategoryValue } : t
            ));
            setShowBulkCategoryPopup(false);
            setBulkCategoryValue("");
            alert(`Updated deposit type for ${targets.length} transaction(s).`);
        } catch (e) {
            console.error('Bulk set deposit type failed:', e);
            alert('Failed to bulk set deposit type.');
        }
    };

    const handleConfirmCheckedRecords = async () => {
        setShowConfirmModal(false);

        const checkedTransactions = filteredTransactions.filter((t) => t.Checked === "Yes");
        const checkedIds = checkedTransactions.map((t) => t.ID);

        if (checkedIds.length === 0) {
            alert("No records selected to confirm.");
            return;
        }

        // Validate unconfirmed checked transactions
        const errors = [];
        for (const t of checkedTransactions.filter(t => t.Confirmed !== "Yes")) {
            const lineItems = getLineItems(t);
            const desc = `${new Date(t["Date (UTC)"]).toLocaleDateString()} - $${Math.abs(parseFloat(t.Amount)).toFixed(2)}`;
            if (lineItems.length === 0) {
                if (!t["Deposit Category"] || String(t["Deposit Category"]).trim() === "") {
                    errors.push(`${desc}: deposit type is required before approval`);
                    continue;
                }
            }
            if (lineItems.length > 0) {
                const itemTotal = lineItems.reduce((sum, item) => sum + (parseFloat(item.amount) || 0), 0);
                const transactionAmount = Math.abs(parseFloat(t.Amount) || 0);
                if (Math.abs(itemTotal - transactionAmount) > 0.01) {
                    errors.push(`${desc}: line item amounts ($${itemTotal.toFixed(2)}) ≠ transaction ($${transactionAmount.toFixed(2)})`);
                }
                for (let i = 0; i < lineItems.length; i++) {
                    if (!lineItems[i].grantCategory) {
                        errors.push(`${desc}: item "${lineItems[i].name || `#${i + 1}`}" has no category assigned`);
                    }
                }
            }
        }
        if (errors.length > 0) {
            alert(`Cannot confirm. The following issues must be fixed:\n\n${errors.join('\n')}`);
            return;
        }

        try {
            const response = await fetch(
                `${process.env.REACT_APP_BACKEND_API_URL}/deposit/confirmChecked`,
                {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                    },
                    credentials: 'include',
                    body: JSON.stringify({ checkedIds }),
                }
            );
            if (!response.ok) {
                throw new Error(`HTTP error! Status: ${response.status}`);
            }
            filterTransactions();
            alert("Checked records confirmed successfully");
        } catch (error) {
            console.error("Error confirming checked records:", error);
            alert("Error confirming checked records.");
        }
    };

    const handleAutoFill = async () => {
        try {
            const response = await fetch(
                `${process.env.REACT_APP_BACKEND_API_URL}/deposit/autofill`,
                {
                    method: "POST",
                    credentials: 'include',
                }
            );
            if (!response.ok) {
                throw new Error(`HTTP error! Status: ${response.status}`);
            }
            filterTransactions();
            alert("Auto-fill completed successfully");
        } catch (error) {
            console.error("Error auto-filling data:", error);
            alert("Error auto-filling data.");
        }
    };

    const handleEditDefaultPaymentClick = (transaction) => {
        if (transaction["Deposit Category"] !== "Refund (Grant Item)") {
            alert(
                'Default Payment can only be edited for "Refund (Grant Item)" deposits.'
            );
            return;
        }

        let defaultPayment = transaction["Default Payment"];

        if (typeof defaultPayment === "string") {
            try {
                defaultPayment = JSON.parse(defaultPayment);
            } catch (error) {
                console.error("Error parsing defaultPayment:", error);
                defaultPayment = {};
            }
        }

        if (typeof defaultPayment !== "object" || defaultPayment === null) {
            defaultPayment = {};
        }

        setSelectedTransaction({
            ...transaction,
            defaultPayment: defaultPayment,
        });
        setShowEditDefaultPaymentPopup(true);
    };

    const handleEditDefaultPaymentSubmit = async (selectedTransaction) => {
        if (!selectedTransaction) {
            alert("No transaction selected");
            return;
        }

        const defaultPaymentKeys = Object.keys(
            selectedTransaction.defaultPayment
        );
        const invalidGrants = defaultPaymentKeys.filter(
            (grant) => !grantList.includes(grant)
        );

        if (invalidGrants.length > 0) {
            const confirmOverride = window.confirm(
                `The current Default Payment contains grants (${invalidGrants.join(
                    ", "
                )}) that are not in the current grant list. Editing this record will override the existing items. Do you want to proceed?`
            );

            if (!confirmOverride) {
                return;
            }
        }

        const totalPercentage = Object.values(
            selectedTransaction.defaultPayment
        ).reduce((sum, percentage) => sum + (parseFloat(percentage) || 0), 0);

        if (Math.abs(totalPercentage - 100) > 0.01) {
            alert(
                `The total percentage must equal 100%. Current total: ${totalPercentage.toFixed(
                    2
                )}%`
            );
            return;
        }

        try {
            const response = await fetch(
                `${process.env.REACT_APP_BACKEND_API_URL}/deposit/defaultPayment/${selectedTransaction.ID}`,
                {
                    method: "PUT",
                    headers: {
                        "Content-Type": "application/json",
                    },
                    credentials: 'include',
                    body: JSON.stringify({
                        id: selectedTransaction.ID,
                        defaultPayment: selectedTransaction.defaultPayment,
                    }),
                }
            );

            if (response.ok) {
                // Auto-unapprove if the transaction was checked
                const originalTransaction = filteredTransactions.find(t => t.ID === selectedTransaction.ID);
                if (originalTransaction && originalTransaction.Checked === "Yes") {
                    try {
                        await fetch(
                            `${process.env.REACT_APP_BACKEND_API_URL}/deposit/check/${selectedTransaction.ID}`,
                            {
                                method: "PUT",
                                credentials: 'include',
                                headers: { "Content-Type": "application/json" },
                                body: JSON.stringify({ Checked: false }),
                            }
                        );
                    } catch (uncheckError) {
                        console.error("Error auto-unapproving transaction:", uncheckError);
                    }
                }
                const updatedTransactions = filteredTransactions.map((t) =>
                    t.ID === selectedTransaction.ID
                        ? {
                              ...t,
                              "Default Payment": JSON.stringify(
                                  selectedTransaction.defaultPayment
                              ),
                              Checked: (originalTransaction && originalTransaction.Checked === "Yes") ? "No" : t.Checked,
                          }
                        : t
                );
                setFilteredTransactions(updatedTransactions);
                setShowEditDefaultPaymentPopup(false);
            } else {
                const errorText = await response.text();
                alert(
                    `Failed to update default payment. Server response: ${errorText}`
                );
            }
        } catch (error) {
            console.error("Error updating default payment:", error);
            alert(`Error updating default payment: ${error.message}`);
        }
    };

    const handleCategoryChange = async (
        transaction,
        category,
        categoryType
    ) => {
        try {
            const updatedTransaction = {
                ...transaction,
                [categoryType]: category,
            };

            if (
                categoryType === "Deposit Category" &&
                category !== "Refund (Grant Item)" &&
                transaction["Deposit Category"] === "Refund (Grant Item)"
            ) {
                updatedTransaction["Default Payment"] = "{}";
            }

            const response = await fetch(
                `${process.env.REACT_APP_BACKEND_API_URL}/deposit/${transaction.ID}`,
                {
                    method: "PUT",
                    headers: {
                        "Content-Type": "application/json",
                    },
                    credentials: 'include',
                    body: JSON.stringify(updatedTransaction),
                }
            );

            if (!response.ok) {
                throw new Error(`HTTP error! Status: ${response.status}`);
            }

            // Auto-unapprove if the transaction was checked
            if (transaction.Checked === "Yes") {
                try {
                    await fetch(
                        `${process.env.REACT_APP_BACKEND_API_URL}/deposit/check/${transaction.ID}`,
                        {
                            method: "PUT",
                            credentials: 'include',
                            headers: { "Content-Type": "application/json" },
                            body: JSON.stringify({ Checked: false }),
                        }
                    );
                } catch (uncheckError) {
                    console.error("Error auto-unapproving transaction:", uncheckError);
                }
            }

            const updatedTransactions = filteredTransactions.map((t) =>
                t.ID === transaction.ID ? { ...updatedTransaction, Checked: transaction.Checked === "Yes" ? "No" : t.Checked } : t
            );
            setFilteredTransactions(updatedTransactions);
        } catch (error) {
            console.error("Error updating category:", error);
            alert("Error updating category.");
        }
    };

    return (
        <div className="max-w-7xl mx-auto p-6 space-y-6">
            <div className="card p-6">
                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                    <h2 className="text-xl font-semibold text-gray-900">Deposit Transactions</h2>
                    <div className="flex flex-wrap gap-3 items-center">
                        <div className="flex items-center gap-2">
                            <label className="text-sm font-medium text-gray-700">Period:</label>
                            <select 
                                value={filterPeriod} 
                                onChange={handleFilterPeriodChange}
                                className="input-field min-w-[120px]"
                            >
                                {getFilterPeriodOptions().map(option => (
                                    <option key={option.value} value={option.value}>{option.label}</option>
                                ))}
                            </select>
                        </div>
                        <button
                            onClick={() => setShowConfirmModal(true)}
                            className="px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-lg hover:bg-green-700 transition-colors"
                        >
                            Submit Approved Records
                        </button>
                        <button
                            onClick={handleAutoFill}
                            className="btn-secondary"
                        >
                            AutoFill
                        </button>
                        <Link to="/customized-deposit-categories">
                            <button className="btn-secondary">
                                Edit Deposit Type/Subcategory
                            </button>
                        </Link>
                    </div>
                </div>
            </div>

            <div className="card overflow-hidden relative z-0">
                <div className="overflow-auto" style={{ maxHeight: '70vh' }}>
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50 sticky top-0 z-30">
                            <tr>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date (UTC)</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Amount</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Bank Description</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    <div className="flex flex-col items-start gap-1">
                                        <span>Deposit Type</span>
                                        <button
                                            onClick={() => setShowBulkCategoryPopup(true)}
                                            className="text-[10px] px-2 py-0.5 bg-blue-600 text-white rounded hover:bg-blue-700 normal-case font-medium"
                                            title="Set deposit type for all unconfirmed transactions"
                                        >
                                            Bulk Set
                                        </button>
                                    </div>
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Subcategory</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Payment (%)</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider sticky right-0 z-20 bg-gray-50" style={{ boxShadow: '-2px 0 4px rgba(0,0,0,0.05)' }}>
                                    <div className="flex flex-col items-start gap-1">
                                        <span>Approve</span>
                                        <button
                                            onClick={() => {
                                                const unconfirmed = filteredTransactions.filter(t => t.Confirmed !== "Yes");
                                                const allChecked = unconfirmed.length > 0 && unconfirmed.every(t => t.Checked === "Yes");
                                                handleSelectAllVisible(!allChecked);
                                            }}
                                            className="text-[10px] px-2 py-0.5 bg-blue-600 text-white rounded hover:bg-blue-700 normal-case font-medium"
                                            title="Toggle select all unconfirmed transactions"
                                        >
                                            Select All
                                        </button>
                                    </div>
                                </th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                        {isLoading ? (
                            // Loading skeleton
                            Array.from({ length: 5 }).map((_, index) => (
                                <tr key={index} className="animate-pulse">
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <div className="h-4 bg-gray-200 rounded w-20"></div>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <div className="h-4 bg-gray-200 rounded w-16"></div>
                                    </td>
                                    <td className="px-6 py-4">
                                        <div className="h-4 bg-gray-200 rounded w-40"></div>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <div className="h-8 bg-gray-200 rounded w-32"></div>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <div className="h-8 bg-gray-200 rounded w-28"></div>
                                    </td>
                                    <td className="px-6 py-4">
                                        <div className="h-4 bg-gray-200 rounded w-24"></div>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <div className="flex gap-2">
                                            <div className="h-6 bg-gray-200 rounded w-20"></div>
                                            <div className="h-6 bg-gray-200 rounded w-16"></div>
                                        </div>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <div className="h-4 w-4 bg-gray-200 rounded"></div>
                                    </td>
                                </tr>
                            ))
                        ) : (
                            filteredTransactions.map((transaction) => (
                            <React.Fragment key={transaction.ID}>
                            <tr
                                className={`hover:bg-gray-50 transition-colors duration-150 ${
                                    transaction.Checked === "Yes"
                                        ? "bg-primary-50 border-l-4 border-primary-400"
                                        : ""
                                }`}
                            >
                                <td className="px-6 py-5 whitespace-nowrap text-sm text-gray-900">
                                    {new Date(
                                        transaction["Date (UTC)"]
                                    ).toLocaleDateString()}
                                </td>
                                <td className="px-6 py-5 whitespace-nowrap text-sm font-medium text-green-600">
                                    ${parseFloat(transaction.Amount).toFixed(2)}
                                </td>
                                <td className="px-6 py-5 text-sm text-gray-900 max-w-xs truncate">{transaction["Bank Description"]}</td>
                                <td className="px-6 py-5 whitespace-nowrap">
                                    {getLineItems(transaction).length > 0 ? (
                                        <span className="text-xs text-gray-400">—</span>
                                    ) : (
                                    <select
                                        value={
                                            transaction["Deposit Category"] ||
                                            ""
                                        }
                                        onChange={(e) =>
                                            handleCategoryChange(
                                                transaction,
                                                e.target.value,
                                                "Deposit Category"
                                            )
                                        }
                                        className="input-field text-sm min-w-[140px]"
                                    >
                                        <option value="">
                                            Select Category
                                        </option>
                                        {depositCategories.map((category) => (
                                            <option
                                                key={category}
                                                value={category}
                                            >
                                                {category}
                                            </option>
                                        ))}
                                    </select>
                                    )}
                                </td>
                                <td className="px-6 py-5 whitespace-nowrap">
                                    {getLineItems(transaction).length > 0 ? (
                                        <span className="text-xs text-gray-400">—</span>
                                    ) : (
                                    <select
                                        value={
                                            transaction[
                                                "Customized Category"
                                            ] || "N/A"
                                        }
                                        onChange={(e) =>
                                            handleCategoryChange(
                                                transaction,
                                                e.target.value,
                                                "Customized Category"
                                            )
                                        }
                                        className="input-field text-sm min-w-[120px]"
                                    >
                                        <option value="N/A">N/A</option>
                                        {(() => {
                                            const selectedDepositType = transaction["Deposit Category"] || '';
                                            const typeSpecific = categoriesByDepositType[selectedDepositType] || [];
                                            const merged = [...new Set(typeSpecific)].filter(c => c !== 'N/A');
                                            return merged.map((category) => (
                                                <option key={category} value={category}>{category}</option>
                                            ));
                                        })()}
                                    </select>
                                    )}
                                </td>
                                <td className="px-6 py-5 text-sm text-gray-900">
                                    {getLineItems(transaction).length > 0 ? (
                                        <span className="text-gray-400">—</span>
                                    ) : transaction["Deposit Category"] ===
                                    "Refund (Grant Item)" ? (
                                        <div className="max-w-xs">
                                            {Object.entries(
                                                JSON.parse(
                                                    transaction[
                                                        "Default Payment"
                                                    ] || "{}"
                                                )
                                            )
                                                .map(
                                                    ([source, percentage]) =>
                                                        `${source}: ${percentage}%`
                                                )
                                                .join(", ")}
                                        </div>
                                    ) : (
                                        <span className="text-gray-400 text-sm">
                                            N/A
                                        </span>
                                    )}
                                </td>
                                <td className="px-6 py-5 whitespace-nowrap">
                                    <div className="flex gap-2">
                                        {transaction["Deposit Category"] ===
                                        "Refund (Grant Item)" ? (
                                            <button
                                                onClick={() =>
                                                    handleEditDefaultPaymentClick(
                                                        transaction
                                                    )
                                                }
                                                className="px-3 py-1.5 text-xs font-medium text-primary-700 bg-primary-50 border border-primary-200 rounded-md hover:bg-primary-100 transition-colors duration-200"
                                            >
                                                Edit Payment
                                            </button>
                                        ) : (
                                            <button
                                                disabled
                                                title="Default Payment only applicable for Grant Item Refunds"
                                                className="px-3 py-1.5 text-xs font-medium text-gray-400 bg-gray-50 border border-gray-200 rounded-md cursor-not-allowed"
                                            >
                                                Edit Payment
                                            </button>
                                        )}
                                        <button
                                            onClick={() =>
                                                handleEditNoteClick(transaction)
                                            }
                                            title={
                                                transaction.Note
                                                    ? transaction.Note
                                                    : "Add note"
                                            }
                                            className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors duration-200 ${
                                                transaction.Note
                                                    ? "text-amber-700 bg-amber-50 border border-amber-200 hover:bg-amber-100"
                                                    : "text-gray-600 bg-gray-50 border border-gray-200 hover:bg-gray-100"
                                            }`}
                                        >
                                            {transaction.Note
                                                ? "Edit Note"
                                                : "Add Note"}
                                        </button>
                                        <button
                                            className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors duration-200 ${getLineItems(transaction).length > 0 ? 'text-blue-700 bg-blue-50 border border-blue-200 hover:bg-blue-100' : 'text-gray-600 bg-gray-50 border border-gray-200 hover:bg-gray-100'}`}
                                            onClick={() => toggleItemization(transaction.ID)}
                                        >
                                            {getLineItems(transaction).length > 0 ? `Items (${getLineItems(transaction).length})` : 'Itemize'}
                                        </button>
                                    </div>
                                </td>
                                <td className={`px-6 py-5 whitespace-nowrap sticky right-0 z-10 ${transaction.Checked === "Yes" ? "bg-primary-50" : "bg-white"}`} style={{ boxShadow: '-2px 0 4px rgba(0,0,0,0.05)' }}>
                                    <input
                                        type="checkbox"
                                        checked={transaction.Checked === "Yes"}
                                        onChange={(e) =>
                                            handleCheckboxChange(
                                                transaction.ID,
                                                e.target.checked
                                            )
                                        }
                                        className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded transition-colors duration-200"
                                    />
                                </td>
                            </tr>
                            {/* Line Items sub-rows */}
                            {expandedItemRows[transaction.ID] && (
                                <>
                                    {getLineItems(transaction).map((item, itemIdx) => (
                                        <tr key={item.id} className="bg-blue-50 border-b border-blue-100">
                                            <td className="pl-8 pr-2 py-1.5 text-xs text-gray-500">
                                                <div className="flex items-center gap-1">
                                                    <span className="text-gray-400">#{itemIdx + 1}</span>
                                                    <input type="text" className="w-24 text-xs border border-gray-300 rounded px-1 py-0.5"
                                                        defaultValue={item.name || ''} placeholder="Name"
                                                        onBlur={(e) => updateLineItem(transaction, item.id, 'name', e.target.value)} />
                                                </div>
                                            </td>
                                            <td className="px-2 py-1.5">
                                                <input type="number" step="0.01" className="w-20 text-xs border border-gray-300 rounded px-1 py-0.5"
                                                    defaultValue={item.amount} placeholder="Amount"
                                                    onBlur={(e) => updateLineItem(transaction, item.id, 'amount', e.target.value)} />
                                            </td>
                                            <td colSpan={2}></td>
                                            <td colSpan={2}>
                                                <button onClick={() => removeLineItem(transaction, item.id)} className="text-red-400 hover:text-red-600 text-xs">Remove</button>
                                            </td>
                                            <td></td>
                                        </tr>
                                    ))}
                                    <tr className="bg-blue-50" style={{ borderBottom: '2px solid #93c5fd' }}>
                                        <td colSpan={20} className="pl-8 pr-4 py-2">
                                            <button onClick={() => addLineItem(transaction)}
                                                className="text-xs text-blue-600 hover:text-blue-800 font-medium flex items-center gap-1">
                                                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                                                </svg>
                                                Add Line Item
                                            </button>
                                        </td>
                                    </tr>
                                </>
                            )}
                            </React.Fragment>
                            ))
                        )}
                        </tbody>
                    </table>
                </div>
            </div>

            {showEditNotePopup && transactionToEditNote && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50 animate-fade-in">
                    <div className="card p-6 max-w-lg w-full animate-slide-up">
                        <h3 className="text-lg font-semibold text-gray-900 mb-4">Edit Transaction Note</h3>
                        <div className="space-y-4">
                            <div className="bg-gray-50 p-4 rounded-lg space-y-2">
                                <p className="text-sm">
                                    <span className="font-medium text-gray-700">Date:</span>{" "}
                                    <span className="text-gray-900">
                                        {new Date(
                                            transactionToEditNote["Date (UTC)"]
                                        ).toLocaleDateString()}
                                    </span>
                                </p>
                                <p className="text-sm">
                                    <span className="font-medium text-gray-700">Description:</span>{" "}
                                    <span className="text-gray-900">
                                        {transactionToEditNote["Bank Description"]}
                                    </span>
                                </p>
                                <p className="text-sm">
                                    <span className="font-medium text-gray-700">Amount:</span>{" "}
                                    <span className="text-green-600 font-medium">
                                        ${parseFloat(
                                            transactionToEditNote.Amount
                                        ).toFixed(2)}
                                    </span>
                                </p>
                            </div>
                            <div className="space-y-2">
                                <label className="block text-sm font-medium text-gray-700">Note:</label>
                                <textarea
                                    value={transactionToEditNote.Note || ""}
                                    onChange={(e) =>
                                        setTransactionToEditNote({
                                            ...transactionToEditNote,
                                            Note: e.target.value,
                                        })
                                    }
                                    placeholder="Add your notes here..."
                                    rows={4}
                                    className="input-field resize-none"
                                />
                            </div>
                            <div className="flex gap-3 mt-6">
                                <button onClick={handleSaveNote} className="btn-primary flex-1">
                                    Save Note
                                </button>
                                <button
                                    onClick={() => setShowEditNotePopup(false)}
                                    className="btn-secondary flex-1"
                                >
                                    Cancel
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {showEditDefaultPaymentPopup && selectedTransaction && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50 animate-fade-in">
                    <EditDefaultPaymentPopup
                        selectedTransaction={selectedTransaction}
                        grantList={grantList}
                        onSave={handleEditDefaultPaymentSubmit}
                        onCancel={() => setShowEditDefaultPaymentPopup(false)}
                    />
                </div>
            )}

            {showFilterPopup && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50 animate-fade-in">
                    <div className="card p-6 max-w-md mx-auto animate-slide-up">
                        <h3 className="text-lg font-semibold text-gray-900 mb-4">Select Custom Period</h3>
                        <div className="space-y-4">
                            <div className="space-y-2">
                                <label className="block text-sm font-medium text-gray-700">Start Date</label>
                                <input
                                    type="date"
                                    value={filterStartDate}
                                    onChange={(e) => setFilterStartDate(e.target.value)}
                                    className="input-field"
                                />
                            </div>
                            <div className="space-y-2">
                                <label className="block text-sm font-medium text-gray-700">End Date</label>
                                <input
                                    type="date"
                                    value={filterEndDate}
                                    onChange={(e) => setFilterEndDate(e.target.value)}
                                    className="input-field"
                                />
                            </div>
                            <div className="flex gap-3 mt-6">
                                <button 
                                    onClick={() => {
                                        setFilterPeriod('custom');
                                        setShowFilterPopup(false);
                                    }}
                                    className="btn-primary flex-1"
                                >
                                    Apply
                                </button>
                                <button 
                                    onClick={() => setShowFilterPopup(false)}
                                    className="btn-secondary flex-1"
                                >
                                    Cancel
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {showBulkCategoryPopup && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50 animate-fade-in">
                    <div className="card p-6 max-w-md w-full animate-slide-up">
                        <h3 className="text-lg font-semibold text-gray-900 mb-4">Bulk Set Deposit Type</h3>
                        <p className="text-sm text-gray-600 mb-4">
                            Apply the selected deposit type to all checked transactions. Line-itemized rows will be skipped.
                        </p>
                        <div className="space-y-2">
                            <label className="block text-sm font-medium text-gray-700">Deposit Type</label>
                            <select
                                value={bulkCategoryValue}
                                onChange={(e) => setBulkCategoryValue(e.target.value)}
                                className="input-field"
                            >
                                <option value="">Select Type</option>
                                {depositCategories.map((category) => (
                                    <option key={category} value={category}>{category}</option>
                                ))}
                            </select>
                        </div>
                        <div className="flex gap-3 mt-6">
                            <button onClick={handleBulkSetCategory} className="btn-primary flex-1">
                                Apply
                            </button>
                            <button
                                onClick={() => { setShowBulkCategoryPopup(false); setBulkCategoryValue(""); }}
                                className="btn-secondary flex-1"
                            >
                                Cancel
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <ConfirmSubmitModal
                isOpen={showConfirmModal}
                onClose={() => setShowConfirmModal(false)}
                onConfirm={handleConfirmCheckedRecords}
            />
        </div>
    );
};

export default DepositTable;
